import Cookies from "js-cookie";
import React from "react";
import { isRole } from "../utils/AccessManagement";
import { DrawRolesEnum } from "../utils/constants";
import { LOW_CONFIDENCE_THRESHOLD } from "../create-loan/ttf-review/constants";
import { tabStatus } from "../create-loan/ttf-review/tabStatus";
import {
  isAdvanceDraw,
  hasInspectionReportDocument
} from "./advanceDrawUtils";

export const getUsername = () => {
  const firstName = Cookies.get("firstName");
  const lastName = Cookies.get("lastName");
  return `${firstName} ${lastName}`;
};

const parseDrawHistoryDate = (date: any) => {
  if (!date) return null;
  const valueDTDate = String(date).replace("T", " ").replace("Z", "");
  const valueDTDateM = valueDTDate ? valueDTDate.slice(0, -7) : "";
  const valueDTDateZoneVal1 = valueDTDate
    ? `${valueDTDateM.replace(/ /g, "T")}Z`
    : "";
  const createdDate = new Date(valueDTDateZoneVal1);
  const dateArray = createdDate.toString().split(" ", 8);
  if (dateArray.length <= 2) return null;
  return dateArray;
};

export const formatAuditTrailTimestamp = (date: any) => {
  const dateArray = parseDrawHistoryDate(date);
  if (!dateArray) return null;
  return {
    dateLine: `${dateArray[2]} ${dateArray[1]} ${dateArray[3]}`,
    timeLine: dateArray[4]
  };
};

export const getDrawDateCreated = (date: any) => {
  const dateArray = parseDrawHistoryDate(date);
  return (
    dateArray && (
      <>
        {dateArray[2]} {dateArray[1]} {dateArray[3]} &bull; {dateArray[4]}
      </>
    )
  );
};

export const sanitizeStringToNum = (abc: string) => {
  try {
    if (typeof abc !== "string") return abc;
    const num = abc?.replace(/,/g, "");
    return Number(num);
  } catch (e) {
    console.error(e);
    return abc;
  }
};

export const isAMTView = () =>
  isRole(DrawRolesEnum.DRAW_MEMBER) || isRole(DrawRolesEnum.DRAW_MANAGER);
export const isOnlyDrawMember = () =>
  isRole(DrawRolesEnum.DRAW_MEMBER) && !isRole(DrawRolesEnum.DRAW_MANAGER);

export const getRoles = (): DrawRolesEnum => {
  let role: DrawRolesEnum;
  if (isAMTView()) {
    if (isOnlyDrawMember()) {
      role = DrawRolesEnum.DRAW_MEMBER;
    } else {
      role = DrawRolesEnum.DRAW_MANAGER;
    }
  } else {
    if (isRole(DrawRolesEnum.ORIGINATOR_DRAW)) {
      role = DrawRolesEnum.ORIGINATOR_DRAW;
    } else {
      role = DrawRolesEnum.ORIGINATOR_VIEW_DRAW;
    }
  }
  return role;
};

export const getAuditTrailDisplayName = (
  updatedBy?: string | null,
  stageName?: string | null
) => {
  const name = updatedBy?.trim();
  if (name) {
    return name;
  }
  if (stageName?.trim().toLowerCase() === "draw submitted") {
    return "System";
  }
  return "Unknown";
};

export const getInitials = (name?: string | null) => {
  const trimmed = name?.trim();
  if (!trimmed) {
    return "?";
  }
  const namesArray = trimmed.toUpperCase().split(/\s+/).filter(Boolean);
  if (namesArray.length >= 2) {
    return (
      namesArray[0].charAt(0) + namesArray[namesArray.length - 1].charAt(0)
    );
  }
  if (trimmed.length >= 2) {
    return trimmed.substring(0, 2).toUpperCase();
  }
  return trimmed.charAt(0).toUpperCase();
};

export const drawSubmittedDate = (drawStageHistory: any, drawStage: any) => {
  const tempObj = JSON.parse(JSON.stringify(drawStageHistory));
  const obj = tempObj.find((o: any) => o.stageName === drawStage);
  return obj?.updatedTimeStamp;
};

export function validateBoxURL(url: string): boolean {
  const regex =
    /^(https:\/\/(?:toorakcapital\.box\.com|toorakcapital\.app\.box\.com)\/(?:folder|file)\/\d+)/;
  return regex.test(url);
}

const INSPECTION_REVIEW_COMPLETE_STATUSES = new Set(["Completed", "Approved", "Approved Conditionally"]);

export function shouldShowTaggingReconcileWarning({
  drawType,
  documentTaggingStatus,
  inspectionReviewStatus,
  inspectionDocs = [],
  requireBothIncomplete = false
}: {
  drawType?: string;
  documentTaggingStatus?: string;
  inspectionReviewStatus?: string;
  inspectionDocs?: any[];
  requireBothIncomplete?: boolean;
}): boolean {
  // Advance-only: do not treat itemWise the same for tagging/reconcile warnings
  if (isAdvanceDraw(drawType) && !hasInspectionReportDocument(inspectionDocs)) {
    return false;
  }

  const taggingIncomplete = documentTaggingStatus !== "Completed";
  const inspectionIncomplete = !INSPECTION_REVIEW_COMPLETE_STATUSES.has(
    inspectionReviewStatus ?? ""
  );

  return requireBothIncomplete
    ? taggingIncomplete && inspectionIncomplete
    : taggingIncomplete || inspectionIncomplete;
}

export const BLOCKED_PREFIXES = ["sitewire-", "trinity-", "mrousavy", "inspectify-"];
export const MEDIA_EXTENSIONS = [
  ".jpg", ".jpeg", ".png", ".gif", ".bmp", ".webp", ".svg", ".heic",
  ".mp4", ".mov", ".avi", ".wmv", ".mkv", ".webm", ".m4v", ".3gp"
];

export function isBlockedDrawDoc(doc: any): boolean {
  const fileName = (doc?.name ?? doc?.fileName ?? "").toLowerCase();
  const normalizedName = fileName.split("?")[0].split("#")[0];
  const baseFileName = normalizedName.split("/").pop() || normalizedName;

  const hasBlockedPrefix = BLOCKED_PREFIXES.some((p) =>
    normalizedName.startsWith(p)
  );
  const isMediaFile = MEDIA_EXTENSIONS.some((ext) =>
    normalizedName.endsWith(ext)
  );
  const hasFileExtension = /\.[^./]+$/.test(baseFileName);

  return hasBlockedPrefix && (isMediaFile || !hasFileExtension);
}

/** Reads confidence from either raw draw docs (`tags`) or review-shaped docs (`TagsInternal`). */
export function getDrawDocumentConfidenceScore(doc: any): number {
  const tags =
    Array.isArray(doc?.TagsInternal) && doc.TagsInternal.length > 0
      ? doc.TagsInternal
      : doc?.tags;
  return Number(tags?.[0]?.confidence_score);
}

const PRIMARY_INSPECTION_OR_ADVANCE_DRAW_TAG_CODES = new Set(["DDDC", "ADRF"]);

function getDrawDocumentTagCode(doc: any): string | undefined {
  const tags =
    Array.isArray(doc?.TagsInternal) && doc.TagsInternal.length > 0
      ? doc.TagsInternal
      : doc?.tags;
  return tags?.[0]?.tag?.code;
}

function isDocMappedToProperty(doc: any): boolean {
  return Array.isArray(doc?.assets) && doc.assets.length > 0;
}

/**
 * Primary inspection (DDDC) or advance draw request (ADRF) that is marked primary
 * and mapped to at least one property via `assets`.
 */
export function hasPrimaryInspectionMappedToProperty(
  docs: any[] = []
): boolean {
  return docs.some(
    (doc) =>
      doc?.isPurchasePrimary === true &&
      isDocMappedToProperty(doc) &&
      PRIMARY_INSPECTION_OR_ADVANCE_DRAW_TAG_CODES.has(
        getDrawDocumentTagCode(doc) ?? ""
      )
  );
}

/**
 * Resolves draw documentTaggingStatus from doc confidence scores.
 * Only resolves when a primary DDDC/ADRF inspection doc is mapped to a property.
 * Same confidence rule as the Document Tagging page: any missing/NaN/<0.9 score => In-Progress;
 * all scores >= 0.9 => Completed. Returns null when there are no docs or no qualifying primary.
 */
export function resolveDrawDocumentTaggingStatus(
  docs: any[] = []
): tabStatus.COMPLETED | tabStatus.INPROGRESS | null {
  const visibleDocs = docs.filter((doc) => !isBlockedDrawDoc(doc));

  if (!visibleDocs.length || !hasPrimaryInspectionMappedToProperty(visibleDocs)) {
    return null;
  }

  const hasLowConfidence = visibleDocs.some((doc) => {
    const score = getDrawDocumentConfidenceScore(doc);
    return Number.isNaN(score) || score < LOW_CONFIDENCE_THRESHOLD;
  });

  return hasLowConfidence ? tabStatus.INPROGRESS : tabStatus.COMPLETED;
}
